/**
 * When to sign somebody in without asking, and when to stop.
 *
 * THE POINT: the firm is already signed in to Entra, so a button that sends somebody somewhere
 * that immediately sends them back exists only to be pressed.
 *
 * IT ONLY WORKS SAME-ORIGIN. An automatic attempt has to be `signInWithRedirect`, since a popup
 * opened without a user gesture is blocked everywhere — and that redirect hands its session back
 * through storage on the `authDomain` origin. See lib/sameOriginAuth.js.
 *
 * LOOPING IS THE RISK WORTH DESIGNING AGAINST. A redirect that fails returns to the same page,
 * which would leave again immediately, with the reason flashing past unread, forever. Two rules
 * prevent it:
 *
 *   ONE AUTOMATIC ATTEMPT PER TAB, marked spent before the browser navigates away, so a failure
 *   lands on the card rather than bouncing straight back out. The button is a deliberate second
 *   attempt and is not subject to it.
 *
 *   SIGNING OUT MEANS STAYING OUT. Somebody signed back in instantly cannot leave, which on a
 *   shared machine defeats the button they were using to protect the next person.
 *
 * Both flags live in sessionStorage: per tab, gone when the browser closes, which is the right
 * lifetime for "we already tried this once just now".
 */

const ATTEMPTED = 'nmi.sso.attempted';
const SIGNED_OUT = 'nmi.sso.signedOut';

/** sessionStorage throws in some privacy modes; never let that break sign-in. */
function read(key) {
  try {
    return window.sessionStorage.getItem(key) === 'yes';
  } catch {
    return false;
  }
}

function write(key, value) {
  try {
    if (value) window.sessionStorage.setItem(key, 'yes');
    else window.sessionStorage.removeItem(key);
  } catch {
    /* the attempt is simply not remembered, which costs one extra redirect at worst */
  }
}

/**
 * @param {object}  args
 * @param {boolean} args.ssoEnabled  false in development, where there is no identity provider
 * @param {boolean} args.sameOrigin  false where the redirect cannot complete
 * @param {boolean} [args.hasError]  a previous attempt already failed in this page's lifetime
 */
export function shouldAttemptAutoSignIn({ ssoEnabled, sameOrigin, hasError = false } = {}) {
  if (!ssoEnabled) return false;
  if (!sameOrigin) return false;
  if (hasError) return false;
  if (read(SIGNED_OUT)) return false;
  if (read(ATTEMPTED)) return false;
  return true;
}

export const markAutoSignInAttempted = () => write(ATTEMPTED, true);
export const markSignedOut = () => write(SIGNED_OUT, true);

/** Somebody pressed the button: an explicit request, so neither flag should stand in its way. */
export function markManualSignIn() {
  write(ATTEMPTED, false);
  write(SIGNED_OUT, false);
}

export { ATTEMPTED, SIGNED_OUT };
